import { RefObject, useCallback, useEffect, useRef } from 'react';
import { wait } from '../../Utils/globals';
import useWindow from './useWindow';

const useFixedElementScroll = (
  offset?: number
): {
  containerRef: RefObject<HTMLDivElement | null>;
  elementRef: RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  onResize: () => void;
} => {
  const window = useWindow();
  const elementRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTop = useRef<number>(0);
  const prevBottom = useRef<number>(0);

  const getContainerWidth = async (iterations: number): Promise<number> => {
    const width = containerRef.current?.clientWidth || 0;
    await wait();
    if (width !== 0 || iterations === 0) {
      return width;
    } else {
      return getContainerWidth(iterations - 1);
    }
  };

  const onResize = useCallback(async (): Promise<void> => {
    if (containerRef.current && elementRef.current) {
      const containerWidth = containerRef.current?.clientWidth || (await getContainerWidth(20));
      elementRef.current.style.width = `${containerWidth}px`;
    }
  }, [containerRef, elementRef]);

  const setStyles = useCallback(
    (position: string, top: string, alignItems: string): void => {
      if (!containerRef.current || !elementRef.current) return;
      elementRef.current.style.position = position;
      elementRef.current.style.top = top;
      containerRef.current.style.alignItems = alignItems;
    },
    [elementRef, containerRef]
  );

  const onScroll = useCallback((): void => {
    if (!containerRef.current || !elementRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const element = elementRef.current.getBoundingClientRect();

    if (container.top <= (offset || 0) && prevTop.current > (offset || 0)) {
      setStyles('fixed', `${offset || 0}px`, 'inherit');
    } else if (container.top >= (offset || 0) && prevTop.current < (offset || 0)) {
      setStyles('relative', 'inherit', 'inherit');
    } else if (
      container.bottom <= element.height + (offset || 0) &&
      prevBottom.current > element.height + (offset || 0)
    ) {
      setStyles('relative', 'inherit', 'flex-end');
    } else if (
      container.bottom >= element.height + (offset || 0) &&
      prevBottom.current < element.height + (offset || 0)
    ) {
      setStyles('fixed', `${offset || 0}px`, 'inherit');
    }
    prevTop.current = container.top;
    prevBottom.current = container.bottom;
  }, [containerRef, elementRef, offset, setStyles]);

  useEffect((): (() => void) => {
    if (containerRef.current && elementRef.current) {
      window?.removeEventListener('resize', onResize);
      window?.addEventListener('resize', onResize);
      onResize();

      window?.document?.removeEventListener('scroll', onScroll);
      window?.document?.addEventListener('scroll', onScroll);

      prevTop.current = containerRef.current.getBoundingClientRect().top;
      prevBottom.current = containerRef.current.getBoundingClientRect().bottom;
    }

    return (): void => {
      window?.removeEventListener('resize', onResize);
      window?.document?.removeEventListener('scroll', onScroll);
    };
  }, [containerRef, elementRef, onResize, onScroll]);

  return { containerRef, elementRef, onScroll, onResize };
};

export default useFixedElementScroll;
